import React from 'react';
import FacebookLogin, {
  ReactFacebookFailureResponse,
  ReactFacebookLoginInfo,
} from 'react-facebook-login';

import { css as cn, cx } from '@emotion/css';
import { css } from '@emotion/react';
import { ErrorPrompt } from '@opendoor/bricks/complex';
import { Box, Flex, Image } from '@opendoor/bricks/core';
import { useTheme } from '@opendoor/bricks/system/theme';
import { colors, fonts, fontSizesNamed, fontWeights, radii } from '@opendoor/bricks/theme/eero';
import {
  colors as colorsNovo,
  fontSizesNamed as fontSizesNamedNovo,
  fonts as fontsNovo,
  media,
} from '@opendoor/bricks/theme/novo';
import { useObservability, UseObservabilityFunctions } from '@opendoor/observability';

import { EventWrapper } from '../../__generated__/protobuf/journey/data/event_wrapper';

// This is a workaround for the fact that the FacebookLogin component is not being exported as a default export
// and the types are incorrect.
// See https://arethetypeswrong.github.io/?p=react-facebook-login%404.1.1
// @ts-expect-error: it does have a default property when imported from mjs
const ResilientFacebookLogin: typeof FacebookLogin = FacebookLogin?.default || FacebookLogin;

const ODFacebookButton = css`
  background-color: ${colors.neutrals0};
  border-radius: ${radii.roundedSquare};
  border: 2px solid ${colors.neutrals100};
  color: ${colors.neutrals100};
  font-family: ${fonts.sans};
  cursor: pointer;
  height: 56px;
  width: 100%;
  font-size: ${fontSizesNamed.s1};
  font-weight: ${fontWeights.bold};
  display: flex;
  align-items: center;
  justify-content: center;

  :hover {
    background-color: ${colors.neutrals30};
  }
`;

const NovoCss = css`
  background-color: transparent;
  border-radius: 18px;
  border: 1.5px solid ${colorsNovo.warmgrey300};
  color: ${colorsNovo.warmgrey950};
  font-family: ${fontsNovo.sans};
  cursor: pointer;

  width: 100%;
  font-size: ${fontSizesNamedNovo['22px']};
  font-weight: 425;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  min-height: 65px;
  ${media.largerThan.SM} {
    min-height: 74px;
  }
  ${media.largerThan.MD} {
    min-height: 82px;
  }

  :hover {
    background-color: ${colorsNovo.warmgrey200};
  }
  :focus {
    outline: 1.5px solid ${colorsNovo.brandBlue600};
    outline-offset: 2px;
  }
  :active {
    transform: scale(0.95);
    backgroundcolor: ${colorsNovo.warmgrey200};
    border: 1.5px solid ${colorsNovo.warmgrey300};
    outline: none;
  }
`;

export type FacebookProfile = {
  name: string;
  email: string;
  id: string;
};

export interface IState {
  isSubmitError: boolean;
}

export interface IProps {
  appId: string;
  cssClass?: string;
  buttonCss: typeof ODFacebookButton;
  size?: 'small' | 'medium' | 'metro';
  iconName?: string;
  textButton?: string;
  fields?: string;
  onAuthed: (response: ReactFacebookLoginInfo) => void;
  onClicked?: (response) => void;
}

export const iconFB = () => {
  return (
    <Image
      height="16px"
      pr={3}
      src="https://imgdrop.imgix.net/b13d96ac33244eb1a52418e84ac5f889.png"
      alt="Facebook logo"
    />
  );
};

class FacebookAuthWithTracking extends React.Component<
  IProps & {
    analytics: UseObservabilityFunctions<undefined, undefined, EventWrapper>;
  },
  IState
> {
  public static defaultProps: Partial<IProps> = {
    cssClass: cx(cn(ODFacebookButton)),
    fields: 'name,email,picture',
    onClicked: () => {
      return {};
    },
    size: 'medium',
    textButton: 'Continue with Facebook',
  };

  public state: IState = {
    isSubmitError: false,
  };

  public render() {
    const { appId, cssClass, fields, size, textButton } = this.props;
    return (
      <Box width="100%">
        <Flex justifyContent="center" alignItems="center">
          <ResilientFacebookLogin
            appId={appId}
            autoLoad={false}
            cssClass={cssClass}
            fields={fields}
            onClick={this.handleClicked}
            callback={this.handleAuth}
            onFailure={this.handleFailure}
            size={size}
            icon={iconFB()}
            textButton={textButton}
            disableMobileRedirect={true}
            containerStyle={{ width: '100%' }}
          />
        </Flex>
        {this.state.isSubmitError && (
          <ErrorPrompt
            message="Sorry, the attempt to sign in with Facebook failed. Please try again."
            paddingBottom="16px"
          />
        )}
      </Box>
    );
  }

  private handleFailure = (response: ReactFacebookFailureResponse) => {
    const { analytics } = this.props;
    this.setState({ isSubmitError: true });

    analytics.trackError('facebook-auth', new Error('failure'), {
      status: response.status,
    });

    analytics.trackEvent(
      'facebook-auth',
      'failed',
      {
        status: response.status,
      },
      { categoryOverride: 'login' },
    );
  };

  private handleAuth = (response) => {
    this.setState({ isSubmitError: false });
    const { onAuthed, analytics } = this.props;
    analytics.trackEvent(
      'facebook-auth',
      'authed',
      {
        email: response.email,
        name: response.name,
      },
      { categoryOverride: 'login' },
    );
    return onAuthed(response);
  };

  private handleClicked = (response) => {
    const { onClicked, analytics } = this.props;
    analytics.trackJourneyEvent('platform', 'buttonClickEvent', {
      targetName: 'facebook-auth',
    });
    return onClicked(response);
  };
}

const ThemedFacbookAuth: React.FC<
  Omit<IProps, 'buttonCss'> & {
    analytics: UseObservabilityFunctions<undefined, undefined, EventWrapper>;
  }
> = (props) => {
  const { name } = useTheme();
  return (
    <FacebookAuthWithTracking
      cssClass={name == 'novo' ? cx(cn(NovoCss)) : cx(cn(ODFacebookButton))}
      {...props}
    />
  );
};

export const FacebookAuth = (props: Omit<IProps, 'buttonCss'>) => {
  const observability = useObservability();
  return <ThemedFacbookAuth analytics={observability} {...props} />;
};
